import { useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel, Th, Td, btn, field } from "@/components/ui-kit";

const ROLES = { manager: "Administrator", uploader: "PO upload" };

const UserDialog = ({ u, custs, close, done }) => {
  const isNew = !u.id;
  const [f, setF] = useState({ email: u.email || "", name: u.name || "", role: u.role || "uploader", active: u.active !== false, customers: u.customers || [], password: "" });
  const set = (k, v) => setF({ ...f, [k]: v });
  const tog = (c) => set("customers", f.customers.includes(c) ? f.customers.filter((x) => x !== c) : [...f.customers, c]);
  const save = async () => {
    try {
      if (isNew) await api.post("/users", f);
      else {
        await api.put(`/users/${u.id}`, f);
        if (f.password) await api.post(`/users/${u.id}/reset-password`, { password: f.password });
      }
      toast.success(`User ${f.email} saved`);
      done();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-lg border-slate-800 bg-[#1F232C] text-slate-100" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New user" : `Edit ${u.email}`}</DialogTitle></DialogHeader>
        {isNew && <Input placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} className={field} data-testid="user-email" />}
        <Input placeholder="Name" value={f.name} onChange={(e) => set("name", e.target.value)} className={field} data-testid="user-name" />
        <select value={f.role} onChange={(e) => set("role", e.target.value)} className={`h-9 rounded-md px-2 text-sm ${field}`} data-testid="user-role">
          {Object.entries(ROLES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {f.role === "uploader" && (
          <div data-testid="user-customers">
            <div className="mb-1 font-mono text-[10px] uppercase text-slate-500">Customers this user can upload POs for</div>
            <div className="grid grid-cols-2 gap-1">{custs.map((c) => (
              <label key={c.code} className="flex items-center gap-2 text-sm"><Checkbox checked={f.customers.includes(c.code)} onCheckedChange={() => tog(c.code)} data-testid={`user-cust-${c.code}`} />{c.name}</label>))}</div>
          </div>)}
        {!isNew && <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.active} onCheckedChange={(v) => set("active", !!v)} data-testid="user-active" /> Active</label>}
        <Input type="password" placeholder={isNew ? "Temporary password (min 8)" : "New password (leave blank to keep)"} value={f.password} onChange={(e) => set("password", e.target.value)} className={field} data-testid="user-password" />
        <p className="text-xs text-slate-500">The user must change a password set here when they next sign in.</p>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={close} className={btn.ghost}>Cancel</Button><Button onClick={save} className={btn.primary} data-testid="user-save">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [custs, setCusts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/users/customers").then((r) => setCusts(r.data)); }, []);
  const name = (c) => custs.find((x) => x.code === c)?.name || c;
  return (
    <div>
      <PageHeader title="Users" subtitle="Administrators see everything. PO upload users only see Upload POs and My summary for the customers ticked on their account.">
        <Button onClick={() => setEdit({})} className={btn.primary} data-testid="users-new-btn"><Plus className="mr-1.5 h-4 w-4" /> New user</Button>
      </PageHeader>
      <Panel>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="border-b border-slate-800"><tr><Th>Email</Th><Th>Name</Th><Th>Role</Th><Th>Customers</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-slate-800/70">{rows.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`}>
              <Td className="font-mono text-xs">{u.email}</Td><Td>{u.name}</Td><Td>{ROLES[u.role] || u.role}</Td>
              <Td className="text-xs">{u.role === "uploader" ? (u.customers || []).map(name).join(", ") : "All"}</Td>
              <Td className="text-xs">{u.active === false ? "Disabled" : "Active"}</Td>
              <Td className="text-right"><Button size="sm" variant="ghost" onClick={() => setEdit(u)} className={btn.ghost} data-testid={`user-edit-${u.email}`}><Pencil className="h-3.5 w-3.5" /></Button></Td>
            </tr>))}</tbody>
        </table></div>
      </Panel>
      {edit && <UserDialog u={edit} custs={custs} close={() => setEdit(null)} done={() => { setEdit(null); load(); }} />}
    </div>
  );
}
